import {
    MessageTemplateSlugEnum,
    SuggestedWorkflowTypeEnum,
    WorkflowTriggerTypeEnum,
} from 'constants/workflows';

interface ReEnrollmentSettings {
    enabled: boolean;
    initialValue: boolean;
}

type WorkflowStarterType = 'trigger' | 'suggested';
interface WorkflowStarter<T, U extends WorkflowStarterType> {
    slug: T;
    name: string;
    description: string;
    icon: string;
    type: U;
    messageTemplateSlug?: string;
    enabled: boolean;
    reEnrollment?: ReEnrollmentSettings;
}

const allWorkflows: WorkflowStarter<SuggestedWorkflowTypeEnum, 'suggested'>[] = [
    {
        slug: SuggestedWorkflowTypeEnum.FIRST_TIME_SUPPORTERS_EMAIL,
        name: 'Create a first-time donor journey',
        description: 'Send an email or series to new supporters who make their first donation.',
        icon: '🎉',
        type: 'suggested',
        messageTemplateSlug: MessageTemplateSlugEnum.FIRST_TIME_SUPPORTERS_EMAIL,
        enabled: true,
    },
    {
        slug: SuggestedWorkflowTypeEnum.DONATIONS_OVER_100_EMAIL,
        name: 'Show appreciation for donations greater than $100',
        description: 'Send an email to supporters for donations that meet a certain threshold.',
        icon: '🎁',
        type: 'suggested',
        messageTemplateSlug: MessageTemplateSlugEnum.DONATIONS_OVER_100_EMAIL,
        enabled: true,
    },
    {
        slug: SuggestedWorkflowTypeEnum.TAG_OVER_1000,
        name: '“Top Supporter” tag for donations greater than $1,000',
        description: 'Track your biggest supporters with a simple tag.',
        icon: '💰',
        type: 'suggested',
        enabled: true,
    },
    {
        slug: SuggestedWorkflowTypeEnum.SIGNUP_WIDGET_SUBSCRIBER_EMAIL,
        name: 'Subscribed via signup widget',
        description: 'Send an email to widget subscribers.',
        icon: '💌',
        type: 'suggested',
        messageTemplateSlug: MessageTemplateSlugEnum.SIGNUP_WIDGET_SUBSCRIBER_EMAIL,
        enabled: true,
    },
    {
        slug: SuggestedWorkflowTypeEnum.ABANDONED_DONATION_EMAIL,
        name: 'Abandoned donation form',
        description: 'Remind donors about their abandoned donation form.',
        icon: '🏃',
        type: 'suggested',
        messageTemplateSlug: MessageTemplateSlugEnum.ABANDONED_DONATION_EMAIL,
        enabled: false,
    },
    {
        slug: SuggestedWorkflowTypeEnum.RECURRING_DONOR_EMAIL,
        name: 'Send a welcome email when someone becomes a recurring donor',
        description:
            'Show appreciation for new recurring donors based on campaign, frequency, or amount.',
        icon: '🔁',
        type: 'suggested',
        messageTemplateSlug: MessageTemplateSlugEnum.RECURRING_DONOR_EMAIL,
        enabled: true,
    },
    {
        slug: SuggestedWorkflowTypeEnum.LAST_GIFT_MADE_EMAIL,
        name: 'Send an email to a donor who hasn’t given in the last year',
        description: 'Email donors who haven’t given in a specific period of time.',
        icon: '⏳',
        type: 'suggested',
        messageTemplateSlug: MessageTemplateSlugEnum.LAST_GIFT_MADE_EMAIL,
        enabled: true,
    },
    {
        slug: SuggestedWorkflowTypeEnum.MAJOR_DONOR_TASK,
        name: 'Follow up with donors who have given over $1,000 dollars',
        description: 'Create tasks to follow-up with donors based on their donation amount.',
        icon: '📝',
        type: 'suggested',
        enabled: true,
    },
    {
        slug: SuggestedWorkflowTypeEnum.FIRST_TIME_DONOR_TASK,
        name: 'Follow up with first-time donors',
        description: 'Create tasks to follow-up with new donors.',
        icon: '📝',
        type: 'suggested',
        enabled: true,
    },
    {
        slug: SuggestedWorkflowTypeEnum.PAST_DONOR_TASK,
        name: 'Follow up with donors who haven’t given in the last 6 months',
        description:
            'Create tasks to follow up with donors who haven’t given in a specific period of time.',
        icon: '📝',
        type: 'suggested',
        enabled: true,
    },
];
const enabledWorkflows = allWorkflows.filter(w => w.enabled);

const allTriggers: WorkflowStarter<WorkflowTriggerTypeEnum, 'trigger'>[] = [
    {
        slug: WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION,
        name: 'First-time donation',
        description: 'New supporters who make their first donation.',
        icon: '🎉',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: false,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.DONATION_CREATED,
        name: 'Donation made',
        description:
            'When a donation is made that is less than, equal to, or greater than a certain amount.',
        icon: '🎁',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.CONTACT_TOTAL_CONTRIBUTIONS,
        name: 'Total contributions',
        description: 'When a supporter has met a certain amount of total contributions.',
        icon: '💰',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: false,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.CONTACT_TAGGED,
        name: 'Tag applied',
        description: 'Occurs when a tag is applied to a contact.',
        icon: '🏷️',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: true,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.PLAN_CREATED,
        name: 'Becomes a recurring donor',
        description: 'Supporters who sign up to become a recurring donor.',
        icon: '🔁',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.PLAN_ANNIVERSARY,
        name: 'Recurring plan anniversary',
        description: 'Supporters whose recurring plan is coming up on its anniversary.',
        icon: '🎂',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.PLAN_CANCELED,
        name: 'Canceled recurring plan',
        description: 'When a supporter cancels their recurring plan.',
        icon: '🚫',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.WIDGET_SUBSCRIBED,
        name: 'Subscribed via signup widget',
        description: 'When a supporter subscribes through your signup widget.',
        icon: '💌',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: false,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM,
        name: 'Abandoned donation form',
        description: 'Donors who were in the process of filling out your donation form but exited.',
        icon: '🏃',
        type: 'trigger',
        enabled: false,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION,
        name: 'Last gift made',
        description: 'Target supporters who haven’t given in a specific period of time.',
        icon: '⏳',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.TEAM_MEMBER_MILESTONE,
        name: 'Team member milestone',
        description: 'Team members who reach a fundraising or supporter milestone.',
        icon: '🎯',
        type: 'trigger',
        enabled: false,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.TEAM_MEMBER_REGISTERED,
        name: 'Register team member',
        description:
            'Team members who complete registration for a peer-to-peer fundraising campaign.',
        icon: '✅',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: false,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.TICKET_CREATED,
        name: 'Issued a ticket',
        description: 'Ticket holders to your event.',
        icon: '🎟️',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.CONTACT_BIRTHDAY,
        name: 'Supporter’s birthday',
        description: 'Celebrate supporters on their birthday.',
        icon: '🎂',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.ATTENDED_EVENT,
        name: 'Attended the event',
        description: 'Thank event attendees or follow up with more information.',
        icon: '🗓️',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.DID_NOT_WIN_AUCTION,
        name: 'Did not win any auction items',
        description:
            'Thank bidders for participating in the auction despite not winning any items.',
        icon: '😔',
        type: 'trigger',
        enabled: false,
        reEnrollment: {
            enabled: true,
            initialValue: false,
        },
    },
    {
        slug: WorkflowTriggerTypeEnum.REGISTERED_FOR_AUCTION,
        name: 'Registered for auction',
        description: 'Supporters who register to bid in an auction.',
        icon: '🪧',
        type: 'trigger',
        enabled: true,
        reEnrollment: {
            enabled: false,
            initialValue: false,
        },
    },
];
const enabledTriggers = allTriggers.filter(t => t.enabled);

const givebutterDataNoticeSlugs = [
    WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION,
    WorkflowTriggerTypeEnum.CONTACT_TOTAL_CONTRIBUTIONS,
    WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION,

    SuggestedWorkflowTypeEnum.FIRST_TIME_SUPPORTERS_EMAIL,
    SuggestedWorkflowTypeEnum.LAST_GIFT_MADE_EMAIL,
];

export { allWorkflows, enabledWorkflows, allTriggers, enabledTriggers, givebutterDataNoticeSlugs };
export type { WorkflowStarter };
